// COGS explorer — prompt-cost lens.
//
// The token cost of an LLM action is dominated by the assembled *input* prompt:
// a fixed template (systemMessage + instructions, editable in the Prompts page)
// plus the per-prospect variables injected at runtime (name, title, company,
// LinkedIn, research notes, FA data…). This module lets the UI price a real
// template against a representative injected payload so you can see how much the
// variables add — and re-price the same prompt under any candidate model.
//
// Tokens are estimated from characters (no tokenizer ships in the SPA). ~4 chars
// per token is a good default for English + Gemini/Claude; it's an editable knob.

import type { ModelRate } from './pricing';

/** Recursively sum the character length of every string in a config object. */
export function textChars(value: unknown): number {
  if (typeof value === 'string') return value.length;
  if (Array.isArray(value)) return value.reduce<number>((sum, v) => sum + textChars(v), 0);
  if (value && typeof value === 'object') {
    return Object.values(value as Record<string, unknown>).reduce<number>((sum, v) => sum + textChars(v), 0);
  }
  return 0;
}

export function tokensFromChars(chars: number, charsPerToken: number): number {
  const cpt = charsPerToken > 0 ? charsPerToken : 4;
  return Math.round(chars / cpt);
}

export interface PromptCostInput {
  baseChars: number; // fixed template (systemMessage + instructions)
  injectedChars: number; // runtime-injected variables for one call
  outputChars: number; // expected generated output
  charsPerToken: number;
  grounded: boolean;
}

export interface PromptCostResult {
  baseInputTokens: number;
  injectedInputTokens: number;
  inputTokens: number;
  outputTokens: number;
  baseInputCost: number;
  injectedInputCost: number;
  inputCost: number;
  outputCost: number;
  groundingCost: number;
  total: number; // per-call cost
  /** Share of input tokens (and of total cost) contributed by injected variables. */
  injectedTokenShare: number;
  injectedCostShare: number;
}

export function promptCost(model: ModelRate | undefined, i: PromptCostInput): PromptCostResult {
  const cpt = i.charsPerToken > 0 ? i.charsPerToken : 4;
  const baseInputTokens = tokensFromChars(i.baseChars, cpt);
  const injectedInputTokens = tokensFromChars(i.injectedChars, cpt);
  const inputTokens = baseInputTokens + injectedInputTokens;
  const outputTokens = tokensFromChars(i.outputChars, cpt);

  const inPerM = model?.inputPerM ?? 0;
  const outPerM = model?.outputPerM ?? 0;
  const baseInputCost = (baseInputTokens / 1e6) * inPerM;
  const injectedInputCost = (injectedInputTokens / 1e6) * inPerM;
  const inputCost = baseInputCost + injectedInputCost;
  const outputCost = (outputTokens / 1e6) * outPerM;
  const groundingCost = i.grounded ? model?.groundingPerCall ?? 0 : 0;
  const total = inputCost + outputCost + groundingCost;

  return {
    baseInputTokens,
    injectedInputTokens,
    inputTokens,
    outputTokens,
    baseInputCost,
    injectedInputCost,
    inputCost,
    outputCost,
    groundingCost,
    total,
    injectedTokenShare: inputTokens ? injectedInputTokens / inputTokens : 0,
    injectedCostShare: total ? injectedInputCost / total : 0,
  };
}

// ── Prompt template registry (token-bearing configs) ──────────────────────────
// Maps each promptable action to its backend config name, the model slot it
// drives (so the lens defaults to the currently-selected model), and whether the
// call is Google-grounded. `configName` matches admin.ts ConfigName.

export interface PromptTemplateRef {
  key: string;
  label: string;
  configName: 'message_prompt' | 'prospect_research_prompt' | 'fa_analysis_prompt' | 'coaching_settings';
  slot: string;
  grounded: boolean;
  /** A representative injected payload, seeded so the lens isn't empty. */
  sampleInjected: string;
}

const SAMPLE_PROSPECT =
  'Name: Marc Anderson\nTitle: VP of Engineering\nCompany: Sana Technologies\n' +
  'LinkedIn: http://www.linkedin.com/in/marc-anderson-1b422a4\n' +
  'Location: Austin, TX\nHeadline: Building developer tools | ex-Stripe, ex-Google\n' +
  'About: 15 years scaling platform teams; passionate about DX and reliability. ' +
  'Recently led a 40-person org through a cloud migration. Angel investor in 6 seed-stage startups.\n' +
  'Recent activity: posted about hiring senior backend engineers; commented on a thread about RSU tax planning.';

export const PROMPT_TEMPLATES: PromptTemplateRef[] = [
  { key: 'research', label: 'Prospect research', configName: 'prospect_research_prompt', slot: 'research_llm', grounded: true, sampleInjected: SAMPLE_PROSPECT },
  { key: 'message', label: 'Message generation', configName: 'message_prompt', slot: 'message_llm', grounded: true, sampleInjected: SAMPLE_PROSPECT },
  { key: 'fa', label: 'FA analysis', configName: 'fa_analysis_prompt', slot: 'fa_llm', grounded: true, sampleInjected: SAMPLE_PROSPECT },
  { key: 'coaching', label: 'AskArchie coaching', configName: 'coaching_settings', slot: 'coaching_llm', grounded: false, sampleInjected: 'User: How should I approach a prospect who just had a liquidity event?\n(+ retrieved RAG context ~2–4k chars)' },
];
